import { useState } from 'react';
import { signInWithEmail, sendPasswordReset } from '../supabase';
import { conTimeout, MSG_TIMEOUT } from '../utils/red';
import { MarcoAcceso, textosEntrada } from './AccesoMarco';
import { AvisoVideoMovil } from './VideoAcceso';
import { ENTRADA } from '../entrada';

// ═══════════════════════════════════════════════════════════════════════════════
// LOGIN (Fase 3) — correo + contraseña contra Supabase Auth.
// El éxito NO se maneja aquí: lo detecta onAuthChange en App.js (SIGNED_IN).
// Este componente solo recoge credenciales y muestra errores genéricos (UX segura).
//
// Modo "reset": solicita el correo de recuperación. La respuesta es UNIFORME —
// nunca revela si la cuenta existe o si tiene casilla real (anti-enumeración).
// Los operativos con correo sintético simplemente no reciben el enlace.
//
// Presentación (sept-2026): ver AccesoMarco.js.
// ═══════════════════════════════════════════════════════════════════════════════

export default function Login({ aviso = '' }) {
  const [mode, setMode] = useState('login');           // 'login' | 'reset'

  const [email,   setEmail]   = useState('');
  const [pass,    setPass]    = useState('');
  const [show,    setShow]    = useState(false);
  const [err,     setErr]     = useState('');
  const [info,    setInfo]    = useState('');
  const [busy,    setBusy]    = useState(false);

  const goLogin = async () => {
    if (!email || !pass) return;
    setBusy(true); setErr(''); setInfo('');
    try {
      // conTimeout: si Auth no contesta, el await nunca volvería y el botón se
      // quedaría en "VERIFICANDO..." para siempre (incidentes 27 y 29/08/2026).
      const { error } = await conTimeout(signInWithEmail(email, pass), 'inicio de sesión');
      if (error) {
        // Mensaje genérico: no distinguir entre "no existe" y "clave incorrecta".
        setErr('Correo o contraseña incorrectos.');
      }
      // Éxito: App.js reacciona al evento SIGNED_IN. No hacemos nada más aquí.
    } catch (e) {
      setErr(e?.esTimeout ? MSG_TIMEOUT : 'Error de conexión. Intenta de nuevo.');
    } finally {
      // En `finally`: pase lo que pase el botón vuelve a estar disponible.
      setBusy(false);
    }
  };

  const goReset = async () => {
    if (!email) return;
    setBusy(true); setErr(''); setInfo('');
    try {
      await conTimeout(sendPasswordReset(email), 'envío de correo de recuperación');
    } catch {
      // Silenciar: la respuesta debe ser uniforme pase lo que pase.
    } finally {
      setBusy(false);
    }
    // Mensaje uniforme SIEMPRE (exista o no la cuenta, tenga o no casilla real).
    setInfo('Si la cuenta tiene un correo registrado, te enviamos un enlace para restablecer la contraseña. Revisa tu bandeja y spam.');
  };

  const onKey = (fn) => (e) => { if (e.key === 'Enter') fn(); };

  const T = textosEntrada();

  return (
    <MarcoAcceso>
          {aviso && <div className="lg-msg aviso">{aviso}</div>}

          {mode === 'login' ? (
            <>
              <h2 className="lg-h">Inicia sesión</h2>
              <p className="lg-sub">{T.sub}</p>

              <div className="lg-campo">
                <label className="lg-label" htmlFor="lg-correo">Correo electrónico</label>
                <input
                  id="lg-correo"
                  className="lg-input"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={e=>{setEmail(e.target.value);setErr('');}}
                  onKeyDown={onKey(goLogin)}
                  placeholder={T.correo}
                  autoFocus
                />
              </div>
              <div className="lg-campo">
                <label className="lg-label" htmlFor="lg-clave">Contraseña</label>
                <div className="lg-clave">
                  <input
                    id="lg-clave"
                    className="lg-input"
                    type={show?'text':'password'}
                    autoComplete="current-password"
                    value={pass}
                    onChange={e=>{setPass(e.target.value);setErr('');}}
                    onKeyDown={onKey(goLogin)}
                  />
                  <button type="button" className="lg-ver" onClick={()=>setShow(!show)}
                          aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                    {show ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
              </div>

              {err && <div className="lg-msg error">{err}</div>}

              <button className="lg-btn" onClick={goLogin} disabled={busy}>
                {busy ? 'Verificando…' : 'Ingresar'}
              </button>

              <div className="lg-centro">
                <button className="lg-link" onClick={()=>{ setMode('reset'); setErr(''); setInfo(''); }}>
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="lg-h">Recuperar contraseña</h2>
              <p className="lg-sub">Ingresa tu correo y te enviaremos un enlace para restablecerla.</p>

              <div className="lg-campo">
                <label className="lg-label" htmlFor="lg-correo-r">Correo electrónico</label>
                <input
                  id="lg-correo-r"
                  className="lg-input"
                  type="email"
                  value={email}
                  onChange={e=>{setEmail(e.target.value);setErr('');setInfo('');}}
                  onKeyDown={onKey(goReset)}
                  placeholder={T.correo}
                  autoFocus
                />
              </div>

              {info && <div className="lg-msg info">{info}</div>}
              {err && <div className="lg-msg error">{err}</div>}

              <button className="lg-btn" onClick={goReset} disabled={busy}>
                {busy ? 'Enviando…' : 'Enviar enlace'}
              </button>

              <div className="lg-centro">
                <button className="lg-link" onClick={()=>{ setMode('login'); setErr(''); setInfo(''); }}>
                  ← Volver al inicio de sesión
                </button>
              </div>
            </>
          )}

          {/* Teléfono, portal médico: invitación a instalarlo como app (oct-2026). */}
          {mode === 'login' && ENTRADA.clave !== 'operaciones' && <AvisoVideoMovil />}

    </MarcoAcceso>
  );
}
